import { formattedDuration } from './durations';
import { roundedPercentageOf } from './numbers';

/**
 * The utilization target every row is judged against.
 *
 * @todo Replace with the account-level utilization target once the backend exposes one. Until then
 * this is the only target in play, so it lives next to the code that judges against it.
 */
export const DEFAULT_UTILIZATION_TARGET = 80;

/** A row is over capacity once it logs more billable time than it has capacity for. */
export const OVER_CAPACITY_UTILIZATION = 100;

export type UtilizationStatus = 'over-capacity' | 'on-target' | 'below-target';

/**
 * Calculates utilization as a percentage of capacity.
 *
 * @returns `undefined` when there's no capacity to divide by, since utilization is meaningless
 * without it.
 */
export const getUtilizationPercentage = (
  billableMinutes: number | undefined,
  capacityMinutes: number | undefined
) => (capacityMinutes ? roundedPercentageOf(billableMinutes ?? 0, capacityMinutes) : undefined);

/**
 * Judges a utilization percentage against the target it should hit.
 */
export const getUtilizationStatus = (
  percentage: number,
  target: number = DEFAULT_UTILIZATION_TARGET
): UtilizationStatus => {
  if (percentage > OVER_CAPACITY_UTILIZATION) return 'over-capacity';
  return percentage >= target ? 'on-target' : 'below-target';
};

/**
 * Spells out the division behind a utilization percentage so the number can be audited.
 */
export const formatUtilizationFormula = (
  billableMinutes: number | undefined,
  capacityMinutes: number | undefined
) =>
  `${formattedDuration(billableMinutes ?? 0, 'traditional')} billable ÷ ${formattedDuration(
    capacityMinutes ?? 0,
    'traditional'
  )} capacity`;
